/* iter55g24 — Consolidated "AI Assist" dropdown for the Editor header.
 *
 * Groups Tone shifts (Lighter / Darker / Action) and Generate actions
 * (Continue / Draft chapter / Draft novel) into ONE dropdown next to
 * the Bible icon so the header stops overflowing on narrow screens.
 *
 * All original data-testids are preserved on the inner buttons so
 * existing tour/Playwright specs keep passing.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 2
import {
  Sparkles, Wand2, Loader2, Lock, BookOpen, Sun, Moon, Zap, ChevronDown, ScrollText, GitBranch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { fmtTokens } from '@/lib/tokenEstimate';

function ToneRow({ label, Icon, busy, disabled, enough, cost, onClick, testid }) {
  return (
    <DropdownMenuItem
      onClick={onClick}
      disabled={busy || disabled}
      data-testid={testid}
      className="flex items-center gap-2 text-[13.5px]"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      <span className="flex-1">{label}</span>
      <span className={`font-mono-stat text-[13px] ${enough ? 'text-muted-foreground' : 'text-amber-500'}`}>
        ~{fmtTokens(cost)}
      </span>
    </DropdownMenuItem>
  );
}

export default function AiAssistDropdown({
  // tone
  toneBusy, onToneShift,
  isDemo, canGenerate, enoughForTone, toneCost,
  // generate
  busy, isStreaming, continueCost, draftCost, remaining, genTitle,
  enoughForContinue, enoughForDraft,
  onContinue, onDraft,
  // draft entire novel
  canBulkDraft = true, onDraftNovel, bulkDraftLockedTitle,
  // Feb-2026 · Generate Synopsis
  onGenerateSynopsis,
  // Plot Builder
  onOpenPlotBuilder,
}) {
  const { t } = useTranslation();
  const locked = !canGenerate || isDemo;
  const bulkLocked = !canBulkDraft || isDemo;
  const anyBusy = busy || toneBusy || isStreaming;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="editor-ai-assist-btn"
          title={anyBusy ? 'AI is busy — please wait…' : t('editor.aiAssistTitle')}
          disabled={anyBusy}
          className="h-6 gap-1 px-1.5 text-[13px] text-primary disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {anyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {t('editor.aiAssist')}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56"
        data-testid="editor-ai-assist-menu"
      >
        <DropdownMenuLabel className="text-[13px] uppercase tracking-wider text-muted-foreground">
          {t('editor.toneShift')}
        </DropdownMenuLabel>
        <ToneRow label={t('editor.toneLighter')} Icon={Sun} busy={toneBusy}
                 disabled={isDemo || !canGenerate}
                 enough={enoughForTone} cost={toneCost}
                 onClick={() => onToneShift('lighter')}
                 testid="editor-tone-lighter-btn" />
        <ToneRow label={t('editor.toneDarker')} Icon={Moon} busy={toneBusy}
                 disabled={isDemo || !canGenerate}
                 enough={enoughForTone} cost={toneCost}
                 onClick={() => onToneShift('darker')}
                 testid="editor-tone-darker-btn" />
        <ToneRow label={t('editor.toneAction')} Icon={Zap} busy={toneBusy}
                 disabled={isDemo || !canGenerate}
                 enough={enoughForTone} cost={toneCost}
                 onClick={() => onToneShift('action')}
                 testid="editor-tone-action-btn" />

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[13px] uppercase tracking-wider text-muted-foreground">
          {t('editor.generate')}
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={onContinue}
          disabled={busy || (!canGenerate && !isDemo)}
          data-testid="editor-continue-btn"
          data-token-enough={enoughForContinue ? '1' : '0'}
          title={genTitle || `Continue prose · est. ~${fmtTokens(continueCost)} tokens (${fmtTokens(remaining)} left)`}
          className="flex items-center gap-2 text-[13.5px]"
        >
          {locked ? <Lock className="h-3.5 w-3.5" /> : busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          <span className="flex-1">{t('editor.continueProse')}</span>
          <span className={`font-mono-stat text-[13px] ${enoughForContinue ? 'text-muted-foreground' : 'text-amber-500'}`}>
            ~{fmtTokens(continueCost)}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onDraft}
          disabled={!canGenerate && !isDemo}
          data-testid="editor-draft-chapter-btn"
          data-token-enough={enoughForDraft ? '1' : '0'}
          title={genTitle || `Draft full chapter · est. ~${fmtTokens(draftCost)} tokens (${fmtTokens(remaining)} left)`}
          className="flex items-center gap-2 text-[13.5px]"
        >
          {locked ? <Lock className="h-3.5 w-3.5" /> : <Wand2 className="h-3.5 w-3.5" />}
          <span className="flex-1">{t('editor.draftChapter')}</span>
          <span className={`font-mono-stat text-[13px] ${enoughForDraft ? 'text-muted-foreground' : 'text-amber-500'}`}>
            ~{fmtTokens(draftCost)}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onDraftNovel}
          data-testid="editor-draft-novel-btn"
          title={bulkLocked
            ? (bulkDraftLockedTitle || 'Draft entire novels — available on Pro Author and above.')
            : 'Draft every remaining chapter in one go. Claude reads what you\'ve written and matches your voice.'}
          className="flex items-center gap-2 text-[13.5px]"
        >
          {bulkLocked ? <Lock className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />}
          <span className="flex-1">{t('editor.draftEntireNovel')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[13px] uppercase tracking-wider text-muted-foreground">
          Story tools
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={onGenerateSynopsis}
          data-testid="editor-generate-synopsis-btn"
          title="Reads whatever chapters you've drafted so far and writes a 3-act synopsis — unwritten parts are clearly marked as inferred."
          className="flex items-center gap-2 text-[13.5px]"
        >
          <ScrollText className="h-3.5 w-3.5" />
          <span className="flex-1">Generate Synopsis</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onOpenPlotBuilder}
          data-testid="editor-plot-builder-btn"
          title="Extract and edit the structured plot of your manuscript — feeds Walker and Drift Correction with precise story context."
          className="flex items-center gap-2 text-[13.5px]"
        >
          <GitBranch className="h-3.5 w-3.5" />
          <span className="flex-1">Plot Builder</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
